<template>
  <div :data-testid="dataTestid">
    <div class="flex items-center gap-2">
      <p class="txt-label-m-semibold text-main" :data-testid="`${dataTestid}-label`">{{ label }}</p>
      <ChipBadge v-if="value !== null" :color="isAbove ? 'success' : 'error'" rounded size="s" :data-testid="`${dataTestid}-status`">
        <template #prepend>
          <ArrowUpIcon v-if="isAbove" class="lucide-icon-s" aria-hidden="true" />
          <ArrowDownIcon v-else class="lucide-icon-s" aria-hidden="true" />
        </template>
        {{ isAbove ? aboveLabel : belowLabel }}
      </ChipBadge>
    </div>
    <div class="mt-2 flex items-center gap-3">
      <div class="h-6 grow">
        <VChart :option="option" :theme="OMR_ECHARTS_THEME" autoresize class="h-full w-full" :data-testid="`${dataTestid}-chart`" />
      </div>
      <span class="shrink-0 txt-body-s-semibold text-main tabular-nums" :data-testid="`${dataTestid}-value`">{{ valueLabel }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ChipBadge } from '@ramp106/omrjs-core-ui'
import { ArrowDownIcon, ArrowUpIcon } from '@ramp106/omrjs-core-ui/icons'
import { computed } from 'vue'

import { OMR_ECHARTS_THEME } from '@/composables/useOmrEchartsTheme'
import { VChart, type OmrEChartsOption } from '@/config/echarts'
import { makeRatioPercent } from '@/helpers/chartFormat'

const props = withDefaults(
  defineProps<{
    label: string
    value: number | null
    comparison: number
    aboveLabel: string
    belowLabel: string
    color: string
    comparisonColor: string
    trackColor: string
    locale: string
    dataTestid?: string
  }>(),
  { dataTestid: 'insights-attribute-bar' },
)

const isAbove = computed(() => props.value !== null && props.value >= props.comparison)

const valueLabel = computed(() => (props.value === null ? '–' : makeRatioPercent(props.locale, 0)(props.value)))

const option = computed<OmrEChartsOption>(() => ({
  grid: { top: 3, left: 0, right: 0, bottom: 3, containLabel: false },
  xAxis: { type: 'value', min: 0, max: 1, show: false },
  yAxis: { type: 'category', data: [''], show: false },
  series: [
    {
      type: 'bar',
      data: [props.value],
      barWidth: 10,
      itemStyle: { color: props.color, borderRadius: [5, 5, 5, 5] },
      showBackground: true,
      backgroundStyle: { color: props.trackColor, borderRadius: [5, 5, 5, 5] },
      markLine: {
        symbol: 'none',
        silent: true,
        data: [{ xAxis: props.comparison }],
        lineStyle: { color: props.comparisonColor, width: 2, type: 'solid' },
        label: { show: false },
      },
    },
  ],
}))
</script>
